import { useState } from 'react';
import { useRouter } from 'next/router';

import { AdornedInput, FormField, FormHelp, Label } from '@zillow/constellation-10';

import { useFormMode } from '../../../contexts/FormModeContext';
import { useScreenSize } from '../../../hooks';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { useMultiEditMutation } from '../../../hooks/useMultiEditMutation';

import { useWizardNavigation } from '../../CreateListingPage/WizardNavigationProvider';

const inputName = 'roomSquareFeet';

const RoomSquareFootageFormField = () => {
  const router = useRouter();
  const { isScreenDesktop } = useScreenSize();
  const formModeContext = useFormMode();
  const navigation = useWizardNavigation();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const floorPlans = listingDetails?.floorPlans || [];
  const floorPlanId = floorPlans[0]?.id;
  const initialRoomSquareFeet = floorPlans[0]?.roomForRent?.roomSquareFeet ?? null;
  const squareFeet = floorPlans[0]?.squareFeet;

  const [customErrorMessage, setCustomErrorMessage] = useState<string | null>(null);

  const updateMutation = useMultiEditMutation();

  const roomSquareFeetField = useFieldSave({
    fieldName: inputName,
    initialValue: initialRoomSquareFeet,
    mutation: updateMutation,
    buildMutationVariables: (value) => {
      return {
        postData: {
          property: {
            listingAlias,
          },
          floorPlans: [
            {
              id: floorPlanId!,
              roomForRent: {
                roomSquareFeet: value,
              },
            },
          ],
        },
      };
    },
  });

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : Number(e.target.value);
    if (value !== null && value > squareFeet) {
      setCustomErrorMessage(
        `‘Room square footage’ field can only be set if the value is less than or equal to the property’s square footage`,
      );
      if (formModeContext.mode === 'wizard') {
        navigation.reportError();
      }
    } else {
      navigation.clearError();
      setCustomErrorMessage(null);
    }

    roomSquareFeetField.onChange(value);
  };

  return (
    <FormField
      label={<Label>Room square footage</Label>}
      error={!!roomSquareFeetField.error || !!customErrorMessage}
      control={
        <AdornedInput
          size={isScreenDesktop ? 'lg' : 'md'}
          input={
            <AdornedInput.Input
              name={inputName}
              value={String(roomSquareFeetField.value || '')}
              onChange={handleFieldChange}
              onBlur={!customErrorMessage ? roomSquareFeetField.onBlur : undefined}
            />
          }
          endAdornment={<AdornedInput.Adornment>sqft</AdornedInput.Adornment>}
        />
      }
      description={<FormHelp>{roomSquareFeetField.error || customErrorMessage}</FormHelp>}
    />
  );
};

export default RoomSquareFootageFormField;
